import moment from "moment";

export default {
  namespaced: true,

  state: {
    currentJourneyId: null,
    currentJourneySerial: -1,
    originalJourney: null,          // 從 API 取得的原始完整 journey 物件（深拷貝）
    journeyObjectArray: [],
    startTimestamp: 0,
    endTimestamp: 0,
    startTimeArray: [],
    climbRateArray: [],
    linkedRoutes: [],              // 第一個斷點的連接相關路線
    pathDayPlans: [],
  },

  getters: {
    journeyData: state => state.originalJourney,

    journeyObjectArray: state => state.journeyObjectArray,

    durationDays: state => state.journeyObjectArray.filter(j => j.type === 'milestone' && j.isRestPoint).length + 1,

    compiledSplitIndexs: state => state.journeyObjectArray.map((j, i) => (j.isRestPoint ? i : -1)).filter(i => i !== -1),

    compiledJourneyArray: state => {
      const result = [];
      let timestamp = state.startTimestamp;
      let rateIndex = 0;

      for (const j of state.journeyObjectArray) {
        const start = timestamp;
        let end = start;

        if (j.type === 'route') {
          const rate = state.climbRateArray[rateIndex] || 1.0;
          const minutes =
              j.routeDirection === 0
                  ? j.detailObject.consuming
                  : j.detailObject.returnConsuming;
          end += parseInt(minutes * 60 * rate) * 1000;
        } else {
          if (j.isRestPoint) {
            // 住宿點直接用startTimeArray的時間
            end = state.startTimeArray[rateIndex + 1];
          }
          else {
            // 休息時間
            end += j.duration * 60 * 1000;
          }
        }

        if (j.isRestPoint) rateIndex++;

        result.push({
          type: j.type,
          serial: j.serial,
          startTimestamp: Number(start),
          endTimestamp: Number(end),
          detail: j.detailObject,
          duration: Math.floor(Number(end) - Number(start)) / ( 60 * 1000 ),
          ... (j.type === 'route' ? { direction: j.routeDirection } : {}),
        });

        timestamp = end;
      }

      return result;
    },

    journeyParts: (state, getter) => {
      const ary = getter.compiledJourneyArray;
      if (!ary.length) return [];

      const parts = [];
      let current = [{
        ...ary[0],
        originIndex: 0,
      }];

      for (let i = 1; i < ary.length; i++) {
        const last = current[current.length - 1];
        const now = {
          ...ary[i],
          originIndex: i,
        };

        if (last.type === 'milestone' && now.type === 'milestone') {
          parts.push(current);
          current = [now];
        } else {
          current.push(now);
        }
      }
      if (current.length > 0) {
        parts.push(current);
      }
      return parts;
    },

    isComplete: (state, getter) => getter.journeyParts.length <= 1,

    linkedRoutes: state => state.linkedRoutes,
  },

  mutations: {
    SET_JOURNEY_ID(state, id) {
      state.currentJourneyId = id
    },

    SET_ORIGINAL_JOURNEY(state, journey) {
      state.originalJourney = journey ? JSON.parse(JSON.stringify(journey)) : null
    },

    SET_FROM_API(state, journey) {
      if (!journey) {
        console.warn('SET_FROM_API: 無效的 journey 資料');
        return;
      }

      state.currentJourneySerial = journey.serial || -1;
      state.endTimestamp = journey.endTimestamp || 0;

      // 起始日期
      state.startTimestamp ||= journey.journey?.[0]?.startTimestamp || 0;

      // 初始化陣列
      state.journeyObjectArray = [];
      state.startTimeArray = [state.startTimestamp];
      state.climbRateArray = journey.climbRateArray ? [...journey.climbRateArray] : [];

      const splitIndex = journey.journeySplitIndexs || [];

      // 逐項解析 journey.journey
      journey.journey?.forEach((j, i) => {
        const isRestPoint = splitIndex.includes(i);

        // 如果是住宿點，下一天的 startTime = 這天的 endTimestamp
        if (isRestPoint) {
          state.startTimeArray.push(j.endTimestamp);
        }

        const jo = {
          type: j.type,
          serial: j.serial,
          duration: j.duration || 0,
          routeDirection: j.type === 'route' ? (j.direction || 0) : 0,
          isRestPoint,
          detailObject: j.detail || {},
        };

        state.journeyObjectArray.push(jo);
      });

      // debug 用，可移除
      console.log('SET_FROM_API 完成解析：', {
        journeyObjectArrayLength: state.journeyObjectArray.length,
        startTimeArray: state.startTimeArray,
        climbRateArray: state.climbRateArray
      });
    },

    SET_REST_POINT(state, { index, isRestPoint }) {
      console.log('index:' + index + ',isRest:' + isRestPoint)
      if (index === -1 || !state.journeyObjectArray[index]) return;

      // 避免重複設定
      if (state.journeyObjectArray[index].isRestPoint === isRestPoint) {
        return;
      }

      // 計算這個 milestone 是第幾天（從 0 開始）
      let dayIndex = 0;
      for (let i = 0; i < index; i++) {
        if (
            state.journeyObjectArray[i].type === 'milestone' &&
            state.journeyObjectArray[i].isRestPoint
        ) {
          dayIndex++;
        }
      }

      if (isRestPoint) {
        // 設成住宿點：插入新值
        const prevRate = state.climbRateArray[dayIndex] || 1.0;
        state.climbRateArray.splice(dayIndex + 1, 0, prevRate);

        // 計算隔天早上 7:00
        const prevStart = state.startTimeArray[dayIndex];
        const newStart = moment(prevStart)
            .add(1, 'days')
            .hour(7)
            .minute(0)
            .second(0)
            .valueOf();

        state.startTimeArray.splice(dayIndex + 1, 0, newStart);

        // 後續全部往後推一天
        for (let i = dayIndex + 2; i < state.startTimeArray.length; i++) {
          const t = moment(state.startTimeArray[i]).add(1, 'days').valueOf();
          state.startTimeArray.splice(i, 1, t);
        }
      } else {
        // 取消住宿點：移除對應項目
        state.climbRateArray.splice(dayIndex + 1, 1);
        state.startTimeArray.splice(dayIndex + 1, 1);

        // 後續全部往前拉一天
        for (let i = dayIndex + 1; i < state.startTimeArray.length; i++) {
          const t = moment(state.startTimeArray[i]).subtract(1, 'days').valueOf();
          state.startTimeArray.splice(i, 1, t);
        }
      }

      // 最後才改 isRestPoint
      state.journeyObjectArray[index].isRestPoint = isRestPoint;
    },

    SET_START_TIMESTAMP(state, timestamp) {
      console.log(timestamp)
      state.startTimestamp = timestamp;
    },

    SET_REST_TIME(state, { index, minutes }) {
      console.log(state.journeyObjectArray[index].duration)
      state.journeyObjectArray[index].duration = minutes;
      console.log(state.journeyObjectArray[index].duration)
    },

    INSERT_AT(state, { index, item }) {
      if (
          index < 0 ||
          index > state.journeyObjectArray.length
      ) {
        throw new Error('INSERT_AT index out of range');
      }

      state.journeyObjectArray.splice(index, 0, item);
    },

    REMOVE_AT(state, index) {
      if (index < 0 || index >= state.journeyObjectArray.length) {
        throw new Error('REMOVE_AT index out of range');
      }
      state.journeyObjectArray.splice(index, 1);
    },

    SET_CLIMB_RATE_AT_INDEX(state, { index, value }) {
      if (
          index < 0 ||
          index >= state.climbRateArray.length
      ) {
        throw new Error(`Invalid climbRateArray index: ${index}`);
      }

      // Vue 2 必須用 splice / Vue.set
      state.climbRateArray.splice(index, 1, value);
    },

    RESET_JOURNEY_STATE(state) {
      state.currentJourneyId = null
      state.originalJourney = null
      state.journeyObjectArray = []
      state.startTimestamp = 0
      state.climbRateArray = []
    },

    // 第一個斷點的連接相關路線
    SET_LINKED_ROUTES(state, routes) {
      state.linkedRoutes = Array.isArray(routes) ? [...routes] : []
    },

    CLEAR_LINKED_ROUTES(state) {
      state.linkedRoutes = []
    },

    SET_PATH_TEMPLATES(state, templates) {
      state.pathDayPlans = templates
    },
  },

  actions: {
    // 載入單一 journey 詳細資料
    async loadJourney({ commit, dispatch }, journeyId) {
      commit('SET_JOURNEY_ID', journeyId)
      try {
        const journey = await dispatch('api/getJourneyDetailPromise', journeyId, { root: true })
        commit('SET_ORIGINAL_JOURNEY', journey)
        commit('SET_FROM_API', journey)

        // 載入 template
        if (journey?.baseOnPathSerial) {
          await dispatch('loadPathDayPlans', journey.baseOnPathSerial)
        }
      } catch (err) {
        console.error('載入 journey 失敗', err)
        throw err
      }
    },

    async loadPathDayPlans({ commit, dispatch }, pathSerial) {
      try {
        const dayTemplatesRequest = await dispatch(
            'api/getPathDayTemplatePromise',
            pathSerial,
            { root: true }
        )

        let templates = []

        for (const dt of dayTemplatesRequest) {
          const templateDetail = await dispatch(
              'api/readJourneyDetailByTemplatePromise',
              dt.serial,
              { root: true }
          )
          templateDetail.tempId = dt.serial
          templateDetail.name = dt.name;
          templates.push(templateDetail)
        }

        templates = templates.sort((a, b) => a.durationDays - b.durationDays)

        commit('SET_PATH_TEMPLATES', templates)

      } catch (err) {
        console.error('loadPathDayPlans 失敗', err)
        throw err
      }
    },

    applyPathTemplate({ state }, template) {
      if (!template) return;

      console.log(template);
      // 1. 重建 journeyObjectArray
      const newArr = template.journey.map((j, index) => ({
        type: j.type,
        serial: j.serial,
        duration: j.duration || 0,
        routeDirection: j.type === 'route' ? (j.direction || 0) : 0,
        isRestPoint: template.journeySplitIndexs.includes(index),
        detailObject: j.detail || {},
      }));

      state.journeyObjectArray = newArr;

      // 2. 重建 split（用 mutation 更好，但先簡化）
      // 3. reset climbRateArray
      const targetLen = template.journeySplitIndexs.length + 1;
      state.climbRateArray = Array(targetLen).fill(1);

      // 4. startTimeArray（你原本邏輯）
      const m = moment(state.startTimestamp);
      state.startTimeArray = [];

      const tempStartM = moment(template.journey[0].endTimestamp);
      m.hour(tempStartM.hour()).minute(tempStartM.minute()).second(tempStartM.second());
      state.startTimeArray.push(m.valueOf());
      state.startTimestamp = m.valueOf();

      for (let i=0;i<template.journeySplitIndexs.length;i++) {
        m.add(1, 'days'); 
        const tempIndex = template.journeySplitIndexs[i];
        const tempM = moment(template.journey[tempIndex].endTimestamp);
        m.hour(tempM.hour()).minute(tempM.minute()).second(tempM.second());
        state.startTimeArray.push(m.valueOf());
      }

    },

    // 確認並儲存編輯結果（這裡只示範前端重置，實際儲存請再加 api call）
    confirmAdvancedEdit({ commit }) {
      // 可在此 dispatch 儲存 API，例如：
      // await dispatch('api/saveJourneyEdit', {
      //   id: state.currentJourneyId,
      //   data: state.editDraft
      // }, { root: true })

      // 儲存成功後重置狀態
      commit('RESET_JOURNEY_STATE')
    },
    async updateJourney({ state, getters, dispatch }) {
      try {
        // 1. 用 compiledJourneyArray（你現在真正的結果來源）
        const compiled = getters.compiledJourneyArray;

        // 2. 過濾成 API 要的格式
        // const filteredJourney = compiled.map(item =>
        //     item.type === 'milestone'
        //         ? {
        //           type: item.type,
        //           serial: item.serial,
        //           startTimestamp: item.startTimestamp,
        //           duration: item.duration,
        //           endTimestamp: item.endTimestamp,
        //         }
        //         : {
        //           type: item.type,
        //           serial: item.serial,
        //           startTimestamp: item.startTimestamp,
        //           duration: item.duration,
        //           endTimestamp: item.endTimestamp,
        //           direction: item.direction,
        //         }
        // );
        const filteredJourney = compiled.map(item => {
          const tmp = { ...item };
          delete tmp.detail;
          return tmp;
        });

        // 3. journeySplitIndexs（從 getters 拿）
        const journeySplitIndexs = getters.compiledSplitIndexs;

        // 4. startDate / endDate（用 timestamp 算）
        const startDate = state.startTimestamp;
        const endDate =
            filteredJourney.length > 0
                ? filteredJourney[filteredJourney.length - 1].endTimestamp
                : startDate;

        // 5. 組 API payload
        const settingsData = {
          serial: state.originalJourney?.serial, // ⚠️ 注意這裡你原本是 journeyData.serial
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD'),
          durationDays:
              state.journeyObjectArray.filter(j => j.type === 'milestone' && j.isRestPoint).length + 1,
          climbRateArray: state.climbRateArray,
          journeySplitIndexs,
          journey: filteredJourney,
        };

        console.log('updateJourney payload:', settingsData);

        // 6. call API
        const res = await dispatch(
            'api/postJourneyUpdatePromise',
            settingsData,
            { root: true }
        );

        console.log(res);
        if(res) {
          // commit('RESET_JOURNEY_STATE')
          await dispatch('loadJourney', state.currentJourneyId);
        }
        // return res;
      } catch (err) {
        console.error('updateJourney 失敗', err);
        throw err;
      }
    },
    setStartDate({ state, dispatch }, dateStr) {
      const m = moment(dateStr, 'YYYY-MM-DD');
      const originM = moment(state.startTimestamp);
      m.startOf('day'); // 確保時間是 00:00:00
      m.hour(originM.hour());
      m.minute(originM.minute());
      m.second(originM.second());
      m.millisecond(originM.millisecond());
      dispatch('setStartTimestamp', m.valueOf());
    },
    setStartTimestamp({ commit, dispatch }, timestamp) {
      commit('SET_START_TIMESTAMP', timestamp);
      dispatch('setSegmentDepartureTime', {
        index: 0,
        leaveTimestamp: timestamp,
      });
    },
    setSegmentDepartureTime({ state, commit }, { index, leaveTimestamp }) {
      console.log('setSegmentDepartureTime index:' + index + ',leaveTimestamp:' + leaveTimestamp);

      const originStartTime = state.startTimeArray[index];
      const m1 = moment(originStartTime).hour(0).minute(0).second(0);
      const m2 = moment(leaveTimestamp).hour(0).minute(0).second(0);
      const diffDays = m2.diff(m1, 'days');
      state.startTimeArray.splice(index, 1, leaveTimestamp);
      for (let i=index+1;i<state.startTimeArray.length;i++) {
        const t = moment(state.startTimeArray[i]);
        t.add(diffDays, 'days');
        state.startTimeArray.splice(i, 1, t.valueOf());
      }
      // const restPoint = state.journeyObjectArray[index];
      // if (index !== 0 && (!restPoint || restPoint.type !== 'milestone' || !restPoint.isRestPoint)) {
      //   throw new Error('只能設定留宿點的出發時間');
      // }
      // ✅ 出發點（index = 0）
      if (index === 0) {
        commit('SET_START_TIMESTAMP', leaveTimestamp);
        return;
      }


      // 防呆
      // if (!restPoint || restPoint.type !== 'milestone' || !restPoint.isRestPoint) {
      //   throw new Error('只能設定留宿點的出發時間');
      // }

      // const compiled = getters.compiledJourneyArray;
      // const originArriveTimestamp = compiled[index].startTimestamp;

      // const diffMinutes = Math.floor(
      //     (leaveTimestamp - originArriveTimestamp) / (60 * 1000)
      // );

      // commit('SET_REST_TIME', {
      //   index,
      //   minutes: diffMinutes,
      // });
    },

    setRestPoint({commit}, {index, isRestPoint}) {
      console.log('index:' + index + ',isRest:' + isRestPoint)
      commit('SET_REST_POINT', {
        index,
        isRestPoint,
      });
    },

    setRestTime({commit}, {index, minutes}) {
      commit('SET_REST_TIME', {
        index,
        minutes: minutes,
      });
    },

    setClimbRateAtIndex({ commit }, { index, value }) {
      commit('SET_CLIMB_RATE_AT_INDEX', {
        index,
        value: Number(value),
      });
    },

    insertJourneyItem({ state, commit }, { index, item }) {
      if (!item || typeof item !== 'object') {
        throw new Error('insertJourneyPoint item 必須是物件');
      }

      if (
          index < 0 ||
          index > state.journeyObjectArray.length
      ) {
        throw new Error('insertJourneyItem index out of range');
      }

      commit('INSERT_AT', {
        index,
        item,
      });
    },

    removeJourneyPoint({ state, commit }, index) {
      const target = state.journeyObjectArray[index];

      if (!target) {
        throw new Error('找不到欲刪除的項目');
      }

      if (target.type === 'milestone') {
        // 若後一個是 route，一併刪除
        if (
            index + 1 < state.journeyObjectArray.length &&
            state.journeyObjectArray[index + 1].type === 'route'
        ) {
          commit('REMOVE_AT', index + 1);
        }

        // 若前一個是 route，一併刪除
        if (
            index - 1 >= 0 &&
            state.journeyObjectArray[index - 1].type === 'route'
        ) {
          commit('REMOVE_AT', index - 1);
          index -= 1; // index 前移，因為前面刪了一個
        }

        commit('REMOVE_AT', index);
        return;
      }

      if (target.type === 'route') {
        commit('REMOVE_AT', index);
        return;
      }

      throw new Error(`不支援刪除 type: ${target.type}`);
    },

    // 離開頁面或重置時呼叫
    clearJourneyData({ commit }) {
      commit('RESET_JOURNEY_STATE')
    },

    // 第一個斷點的連接相關路線
    setLinkedRoutes({ commit }, routes) {
      commit('SET_LINKED_ROUTES', routes)
    },

    clearLinkedRoutes({ commit }) {
      commit('CLEAR_LINKED_ROUTES')
    },
  }
}